import { BackTop, Table, Card, Row, Col, Statistic } from "antd";
import React, { useState, useEffect } from "react";
import DiagnosisFilterBar from "../../../components/filter-bars/diagnosis-report-filters";
import { calcAge, exportToExcel } from "../../../helpers/utilities";

export default function DiagnosisReport({ data, isLoading }) {
  const [filteredData, setFilteredData] = useState([]);

  const handleReset = () => {
    setFilteredData(data);
  };

  const handleApplyFilter = (newData) => {
    setFilteredData(newData);
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Calculate metrics for the current filtered data
  const calculateMetrics = () => {
    const totalDiagnoses = filteredData?.length;

    // Count unique diagnoses
    const uniqueDiagnoses = [
      ...new Set(filteredData?.map((diagnosis) => diagnosis.diagnosis)),
    ].length;

    // Count unique patients
    const uniquePatients = [
      ...new Set(
        filteredData?.map(
          (diagnosis) =>
            `${diagnosis.consultation?.patient?.firstName} ${diagnosis.consultation?.patient?.lastName}`
        )
      ),
    ].length;

    // Count unique doctors
    const uniqueDoctors = [
      ...new Set(
        filteredData?.map(
          (diagnosis) =>
            `${diagnosis.consultation?.doctor?.firstName} ${diagnosis.consultation?.doctor?.lastName}`
        )
      ),
    ].length;

    // Count consultation types
    const uniqueConsultationTypes = [
      ...new Set(
        filteredData?.map((diagnosis) => diagnosis.consultation?.type)
      ),
    ].length;

    // Calculate average age
    const totalAge = filteredData?.reduce((sum, diagnosis) => {
      const age = calcAge(diagnosis.consultation?.patient?.dob).age;
      return sum + (isNaN(age) ? 0 : age);
    }, 0);
    const averageAge =
      totalDiagnoses > 0 ? Math.round(totalAge / totalDiagnoses) : 0;

    // Count by gender
    const malePatients = filteredData?.filter(
      (diagnosis) => diagnosis.consultation?.patient?.gender === "Male"
    )?.length;
    const femalePatients = filteredData?.filter(
      (diagnosis) => diagnosis.consultation?.patient?.gender === "Female"
    )?.length;

    return {
      totalDiagnoses,
      uniqueDiagnoses,
      uniquePatients,
      uniqueDoctors,
      uniqueConsultationTypes,
      averageAge,
      malePatients,
      femalePatients,
    };
  };

  const columns = [
    {
      title: "#",
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => ++index,
    },
    {
      title: "Date - Time",
      sorter: (a, b) => a?.createdAt > b?.createdAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        new Date(record?.createdAt).toUTCString(),
    },
    {
      title: "Consultation",
      sorter: (a, b) => a?.consultation?.type > b?.consultation?.type,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record?.consultation?.type} - (${record?.consultation?.code})`,
    },
    {
      title: "Doctor",
      sorter: (a, b) =>
        a?.consultation?.doctor?.firstName > b?.consultation?.doctor?.firstName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record?.consultation?.doctor?.firstName} ${record?.consultation?.doctor?.lastName}`,
    },
    {
      title: "Diagnosis",
      sorter: (a, b) => a?.diagnosis > b?.diagnosis,
      dataIndex: "diagnosis",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Patient Name",
      sorter: (a, b) =>
        a?.consultation?.patient?.firstName >
        b?.consultation?.patient?.firstName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record?.consultation?.patient?.firstName}  ${record?.consultation?.patient?.lastName}`,
    },
    {
      title: "Gender",
      sorter: (a, b) =>
        a?.consultation?.patient?.gender > b?.consultation?.patient?.gender,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => record?.consultation?.patient?.gender,
    },
    {
      title: "Age",
      sorter: (a, b) => a.calcAge(a?.consultation?.patient?.dob).age,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        calcAge(record?.consultation?.patient?.dob).age,
    },
    {
      title: "Insurance Provider",
      dataIndex: "gender",
      sorter: (a, b) =>
        a?.consultation?.patient?.insuranceProvider?.providerName >
        b.consultation?.patient?.insuranceProvider?.providerName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.consultation?.patient?.insuranceProvider?.providerName,
    },

    // {
    //     title: "Attachments",
    //     sorter: ( a, b ) => a.totalConsultations > b.totalConsultations,
    //     dataIndex: "totalConsultations",
    //     sortDirections: [ "descend", "ascend" ],
    // },
  ];

  const metrics = calculateMetrics();

  return (
    <div>
      <DiagnosisFilterBar
        data={data}
        onReset={handleReset}
        onFilter={(d) => handleApplyFilter(d)}
        excelExport={() =>
          exportToExcel("diagnosisReportTable", "rivia-diagnosis-report")
        }
      />

      {/* Metrics Cards */}
      <Row gutter={16} className="mt-3 mb-3">
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Diagnoses"
              value={metrics.totalDiagnoses}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Unique Diagnoses"
              value={metrics.uniqueDiagnoses}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Unique Patients"
              value={metrics.uniquePatients}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Doctors"
              value={metrics.uniqueDoctors}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Consultation Types"
              value={metrics.uniqueConsultationTypes}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Average Age"
              value={metrics.averageAge}
              suffix="years"
              valueStyle={{ color: "#eb2f96" }}
            />
          </Card>
        </Col>
      </Row>

      {/* advanced filters
            
            -> date range
            -> age
            -> agent
            
            */}
      <Table
        className="border mt-3"
        id="diagnosisReportTable"
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        bordered
      />
      <BackTop />
    </div>
  );
}
